import { Node, mergeAttributes } from '@tiptap/core';
import { syncIdAttributes } from '../../extensions/CustomUniqueId';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceAroundStep, ReplaceStep } from '@tiptap/pm/transform';
import { MutableRefObject } from 'react';
import { TEMPLATE_FIELD_DROPDOWN_NAME, templateFieldWrapperClass } from '../../../doc-engine/templateFields';
import { DropdownOptionsStep } from './DropdownOptionsStep';

type TOptions = {
  isTemplate?: boolean;
  refTemplateDropdownOptions?: MutableRefObject<Record<string, string[]>>;
};

const TemplateFieldDropdownNode = Node.create<TOptions>({
  name: TEMPLATE_FIELD_DROPDOWN_NAME,

  group: 'inline inlineTemplateField',
  inline: true,
  atom: true,
  draggable: true,

  // `id` and `fieldId` come from the `addUniqueIdAttribute()` wrapper, not from this node.

  addOptions() {
    return {
      isTemplate: false,
      refTemplateDropdownOptions: undefined,
    };
  },

  addAttributes() {
    return {
      selectedValue: {
        default: '',
        parseHTML: (element: HTMLElement) => element.textContent?.trim() || '',
        // Not rendered as an HTML attribute - serialized as text content in renderHTML.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'template-field-dropdown' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['template-field-dropdown', mergeAttributes(HTMLAttributes), node.attrs.selectedValue || ''];
  },

  onTransaction({ editor, transaction, appendedTransactions }) {
    if (!this.options.isTemplate || !this.options.refTemplateDropdownOptions) return;
    if (
      transaction.getMeta('preventUpdate') ||
      !transaction.steps.some((step) => step instanceof DropdownOptionsStep) ||
      !transaction.before.eq(editor.state.doc)
    ) {
      return;
    }
    // TipTap skips update events when content is equal. Options-only undo/redo still needs
    // to notify autosave; content-changing transactions already emit their own update.
    editor.emit('update', { editor, transaction, appendedTransactions });
  },

  addProseMirrorPlugins() {
    // Selection highlight for atom nodes is handled centrally by AtomSelectionHighlight.
    // A dropdown inside another field is the block field's nesting guard.
    const plugins: Plugin[] = [];

    // In template mode: restore AI options with history and initialize orphan dropdown nodes
    // (e.g. after copy/paste with regenerated fieldId) from inserted/restored content only.
    if (this.options.isTemplate && this.options.refTemplateDropdownOptions) {
      const nodeType = this.type;
      const refOptions = this.options.refTemplateDropdownOptions;

      plugins.push(
        new Plugin({
          key: new PluginKey('templateFieldDropdownOrphan'),
          state: {
            init: () => null,
            apply(transaction, value) {
              transaction.steps.forEach((step) => {
                if (!(step instanceof DropdownOptionsStep)) return;
                Object.entries(step.after).forEach(([fieldId, options]) => {
                  // Manual option edits bypass history; preserve them if the snapshot no longer matches.
                  const current = refOptions.current[fieldId];
                  const expected = step.before[fieldId];
                  if (
                    current?.length !== expected?.length ||
                    current?.some((option, index) => option !== expected?.[index])
                  ) {
                    return;
                  }
                  if (options) refOptions.current[fieldId] = [...options];
                  else delete refOptions.current[fieldId];
                });
              });
              return value;
            },
          },
          appendTransaction(transactions) {
            // Only run when there are actual doc changes (not just selection changes).
            if (!transactions.some((tr) => tr.docChanged)) return null;

            for (const tr of transactions) {
              for (const step of tr.steps) {
                if (!(step instanceof ReplaceStep || step instanceof ReplaceAroundStep)) continue;
                if (!step.slice.content.size) continue;

                step.slice.content.descendants((node) => {
                  if (node.type !== nodeType) return true;
                  const fieldId = node.attrs.fieldId;
                  if (!fieldId || refOptions.current[fieldId]) return false;

                  // Backend validation requires at least one option for dropdown fields.
                  refOptions.current[fieldId] = [node.attrs.selectedValue || 'Option 1'];
                  return false;
                });
              }
            }

            return null; // no doc changes needed, only ref mutation
          },
        })
      );
    }

    return plugins;
  },

  addNodeView() {
    const refOptions = this.options.refTemplateDropdownOptions;

    return ({ node }) => {
      const wrapper = document.createElement('span');
      wrapper.className = templateFieldWrapperClass(TEMPLATE_FIELD_DROPDOWN_NAME);
      syncIdAttributes(wrapper, node);
      wrapper.setAttribute('data-node-view-wrapper', '');

      const valueSpan = document.createElement('span');
      valueSpan.className = 'template-field-dropdown-value';
      valueSpan.textContent = node.attrs.selectedValue || '';

      wrapper.append(valueSpan);

      // Toggle visual indicator when selectedValue is not in the configured options.
      // Called on init and on update - update() runs after the no-op transaction that
      // handleOptionsChange dispatches, so it picks up ref mutations.
      const syncOrphanedState = (n: typeof node): void => {
        if (!refOptions?.current) return;
        const opts = refOptions.current[n.attrs.fieldId];
        const isOrphaned = !!n.attrs.selectedValue && !!opts?.length && !opts.includes(n.attrs.selectedValue);
        wrapper.classList.toggle('template-field-dropdown-orphaned', isOrphaned);
      };

      syncOrphanedState(node);

      return {
        dom: wrapper,
        contentDOM: null, // atom - no editable content
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;
          syncIdAttributes(wrapper, updatedNode);
          valueSpan.textContent = updatedNode.attrs.selectedValue || '';
          syncOrphanedState(updatedNode);
          return true;
        },
      };
    };
  },
});

export default TemplateFieldDropdownNode;
